// src/pages/SolutionsPage.js

import React, { useContext, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { ThemeContext } from '../context/ThemeContext';
import { solutionService } from '../api';
import SectionTitle from '../components/common/SectionTitle';
import PageLoader from '../components/common/PageLoader';
import ErrorDisplay from '../components/common/ErrorDisplay';

// Animations variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.3
    }
  }
};

const itemVariants = {
  hidden: { y: 50, opacity: 0 },
  visible: { 
    y: 0, 
    opacity: 1,
    transition: { duration: 0.6, ease: "easeOut" }
  }
};

// Styled components
const SolutionsSection = styled.section`
  padding: 6rem 2rem;
  background: ${props => props.isDark 
    ? 'rgba(15, 23, 42, 0.5)' 
    : 'rgba(248, 250, 252, 0.7)'
  };
  position: relative;
  z-index: 1;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${props => props.isDark 
      ? 'linear-gradient(180deg, rgba(10, 15, 30, 0.7) 0%, rgba(20, 30, 60, 0.6) 100%)' 
      : 'linear-gradient(180deg, rgba(255, 255, 255, 0.8) 0%, rgba(240, 245, 255, 0.7) 100%)'
    };
    z-index: -1;
    backdrop-filter: blur(5px);
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const SolutionsList = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 6rem;
  margin-top: 4rem;
`;

const SolutionItem = styled(motion.div)`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;
  position: relative;
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
  
  &:after {
    content: '';
    position: absolute;
    bottom: -3rem;
    left: 10%;
    width: 80%;
    height: 1px;
    background: ${props => props.isDark 
      ? 'rgba(255, 255, 255, 0.1)' 
      : 'rgba(0, 0, 0, 0.1)'
    };
  }
  
  &:last-child:after {
    display: none;
  }
`;

const SolutionImageWrapper = styled.div`
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  object-fit: cover;
  background-size: cover;
  background-position: center;
  box-shadow: ${props => props.isDark 
    ? '0 20px 40px rgba(0, 0, 0, 0.5), 0 0 15px rgba(66, 153, 225, 0.15)' 
    : '0 20px 40px rgba(0, 0, 0, 0.1), 0 0 15px rgba(66, 153, 225, 0.1)'
  };
  transform: perspective(1000px) rotateY(${props => props.isEven ? '3deg' : '-3deg'});
  transition: transform 0.6s ease;
  
  &:hover {
    transform: perspective(1000px) rotateY(0);
  }
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      to bottom,
      rgba(0, 0, 0, 0.1),
      rgba(0, 0, 0, 0.3)
    );
    z-index: 1;
  }
  
  @media (max-width: 992px) {
    transform: none;
  }
`;

const SolutionImage = styled.div`
  width: 100%;
  height: 450px;
  background-image: url(${props => props.src || '/placeholder-image.jpg'});
  background-size: contain; 
  background-repeat: no-repeat; 
  background-position: center; 
  transition: transform 0.5s ease;

  &:hover {
    transform: scale(1.05);
  }

  @media (max-width: 992px) {
    height: 300px;
  }
`;


const SolutionContent = styled(motion.div)`
  display: flex;
  flex-direction: column;
`;

const SolutionTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  color: ${props => props.isDark ? '#f1f5f9' : '#1e293b'};
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -0.5rem;
    left: 0;
    width: 60px;
    height: 4px;
    background: ${props => props.primary || '#3b82f6'};
    border-radius: 2px;
  }
`;

const SolutionDescription = styled.p`
  margin-bottom: 2rem;
  color: ${props => props.isDark ? '#cbd5e1' : '#475569'};
  line-height: 1.8;
  font-size: 1.1rem;
`;

const TechTagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin: 1.5rem 0;
`;

const TechTag = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.5rem 1rem;
  background: ${props => props.isDark 
    ? 'rgba(59, 130, 246, 0.15)'
    : 'rgba(59, 130, 246, 0.08)'
  };
  color: ${props => props.isDark ? '#93c5fd' : '#2563eb'};
  border-radius: 50px;
  font-size: 0.9rem;
  font-weight: 500;
  box-shadow: ${props => props.isDark 
    ? '0 2px 4px rgba(0, 0, 0, 0.2)' 
    : '0 2px 4px rgba(0, 0, 0, 0.05)'
  };
  
  &:before {
    content: '';
    display: inline-block;
    width: 6px;
    height: 6px;
    background: ${props => props.isDark ? '#93c5fd' : '#2563eb'};
    border-radius: 50%;
    margin-right: 8px;
  }
`;

const DiscoverButton = styled.a`
  display: inline-flex;
  align-items: center;
  padding: 0.9rem 1.8rem;
  background-color: ${props => props.primary || '#3b82f6'};
  color: white;
  text-decoration: none;
  border-radius: 50px;
  font-weight: 600;
  font-size: 1rem;
  transition: all 0.3s ease;
  align-self: flex-start;
  position: relative;
  overflow: hidden;
  box-shadow: ${props => props.isDark 
    ? '0 4px 12px rgba(59, 130, 246, 0.4)' 
    : '0 4px 12px rgba(59, 130, 246, 0.25)'
  };
  
  &:after {
    content: "→";
    margin-left: 0.5rem;
    transition: transform 0.3s ease;
  }
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(255, 255, 255, 0.1);
    transform: translateX(-100%);
    transition: transform 0.3s ease;
  }
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: ${props => props.isDark 
      ? '0 8px 16px rgba(59, 130, 246, 0.5)' 
      : '0 8px 16px rgba(59, 130, 246, 0.3)'
    };
    
    &:after {
      transform: translateX(3px);
    }
    
    &:before {
      transform: translateX(0);
    }
  }
`;

const NoResults = styled(motion.div)`
  text-align: center;
  padding: 5rem 2rem;
  color: ${props => props.isDark ? '#f1f5f9' : '#1e293b'};
  background: ${props => props.isDark 
    ? 'rgba(15, 23, 42, 0.5)' 
    : 'rgba(241, 245, 249, 0.7)'
  };
  backdrop-filter: blur(5px);
  border-radius: 12px;
  box-shadow: ${props => props.isDark 
    ? '0 8px 30px rgba(0, 0, 0, 0.3)' 
    : '0 8px 30px rgba(0, 0, 0, 0.05)'
  };
  margin-top: 2rem;
  border: 1px solid ${props => props.isDark 
    ? 'rgba(59, 130, 246, 0.2)' 
    : 'rgba(59, 130, 246, 0.1)'
  };
`;

const NoResultsIcon = styled.div`
  font-size: 5rem;
  margin-bottom: 1.5rem;
  color: ${props => props.primary || '#3b82f6'};
  opacity: 0.7;
`;

const SolutionsPage = () => {
  const { theme, isDarkMode } = useContext(ThemeContext);
  const { t } = useTranslation();
  const [solutions, setSolutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Référence pour l'animation avec IntersectionObserver
  const [ref, inView] = useInView({
    triggerOnce: false,
    threshold: 0.15,
    rootMargin: "-100px 0px",
  });
  
  // Charger les solutions depuis l'API
  useEffect(() => {
    const fetchSolutions = async () => {
      try {
        setLoading(true);
        
        // Appel à l'API pour récupérer les solutions
        const response = await solutionService.getAll();
        
        // Vérifier la structure de la réponse
        let solutionsData = [];
        if (response.data && Array.isArray(response.data.results)) {
          solutionsData = response.data.results;
        } else if (Array.isArray(response.data)) {
          solutionsData = response.data;
        }
        
        setSolutions(solutionsData);
        
        // Mise à jour du titre de la page
        document.title = `${t('ourSolutions')} | PropentaTech`;
      } catch (err) {
        console.error("Error fetching solutions:", err);
        setError(err.response?.data?.detail || t('errorLoadingSolutions'));
      } finally {
        setLoading(false);
      }
    };
    
    fetchSolutions();
    
    return () => {
      document.title = 'PropentaTech';
    };
  }, [t]);
  
  // Extraire les technologies en tableau
  const getTechTags = (technologies) => {
    if (!technologies) return [];
    return typeof technologies === 'string' 
      ? technologies.split(',').map(tech => tech.trim())
      : Array.isArray(technologies) 
        ? technologies 
        : [];
  };
  
  if (loading) {
    return <PageLoader />;
  }
  
  if (error) {
    return <ErrorDisplay error={error} />;
  }
  
  return (
    <SolutionsSection isDark={isDarkMode}>
      <Container>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <SectionTitle
            title={t('ourSolutions')}
            subtitle={t('ourSolutionsSubtitle')}
          />
        </motion.div>
        
        {solutions.length === 0 ? (
          <NoResults 
            isDark={isDarkMode}
            primary={theme.primary}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
          >
            <NoResultsIcon primary={theme.primary}>
              <i className="fas fa-puzzle-piece"></i>
            </NoResultsIcon>
            <h3>{t('noSolutionsFound')}</h3>
            <p>{t('checkBackLater')}</p>
          </NoResults>
        ) : (
          <SolutionsList
            ref={ref}
            variants={containerVariants}
            initial="hidden"
            animate={inView ? "visible" : "hidden"}
          >
            {solutions.map((solution, index) => {
              const isEven = index % 2 === 1;
              
              return (
                <SolutionItem
                  key={solution.id}
                  variants={itemVariants}
                  isDark={isDarkMode}
                >
                  <div style={{ order: isEven && window.innerWidth > 992 ? 2 : 1 }}>
                    <SolutionImageWrapper isDark={isDarkMode} isEven={isEven}>
                      <SolutionImage src={solution.image} />
                    </SolutionImageWrapper>
                  </div>
                  
                  <SolutionContent style={{ order: isEven && window.innerWidth > 992 ? 1 : 2 }}>
                    <SolutionTitle 
                      isDark={isDarkMode}
                      primary={theme.primary}
                    >
                      {solution.name || solution.title}
                    </SolutionTitle>
                    
                    <SolutionDescription isDark={isDarkMode}>
                      {solution.short_description || solution.description?.substring(0, 200) + '...'}
                    </SolutionDescription>
                    
                    {getTechTags(solution.technologies).length > 0 && (
                      <TechTagsContainer>
                        {getTechTags(solution.technologies).map((tech, i) => (
                          <TechTag key={i} isDark={isDarkMode}>{tech}</TechTag>
                        ))}
                      </TechTagsContainer>
                    )}
                    
                    <DiscoverButton 
                      href={`/solutions/${solution.id}`}
                      primary={theme.primary}
                      isDark={isDarkMode}
                    >
                      {"Voir la solution"}
                    </DiscoverButton>
                  </SolutionContent>
                </SolutionItem>
              );
            })}
          </SolutionsList>
        )}
      </Container>
    </SolutionsSection>
  );
};

export default SolutionsPage;